import { useCallback, useEffect, useState } from "react";
import { HOME_ROUTE, STATISTICS_ROUTE } from "../../app/router/routes";
import { useAppSelector } from "../../app/store/store";
import { calendarService, IGetCalendarFilterOptionsResponse } from "../../entities/calendar";
import { FEATURES, FEATURE_SECTIONS, hasFeature, useMyActions } from "../../entities/my";
import { EMPTY_STATISTICS_SUMMARY, ICancelledLessonItem, IRegularScheduleLossItem, IStatisticsMetric, IStatisticsSummary, statisticsService, TStatisticsFaultParty } from "../../entities/statistics";
import { useGlobalMessageActions } from "../../entities/globalMessage";
import { AuthError } from "../../shared/lib/error/AuthError";
import { MyError } from "../../shared/lib/error/MyError";
import { getStatisticsRange, getStatisticsWeek } from "./statisticsPeriod";
import { AccessDeniedRedirect } from "../../shared/ui/accessDeniedRedirect";
import { MyButton, MyButtonSecondary } from "../../shared/ui/button";
import { MyInput } from "../../shared/ui/input";
import { Modal } from "../../shared/ui/modal";
import { DropDownListSelected } from "../../shared/ui/dropDownSelected";
import { LayoutPages } from "../layoutPages";
import classes from './statistics.module.scss'

const getDefaultFrom = () => getStatisticsWeek().from
const getDefaultTo = () => getStatisticsWeek().to

const formatHours = (value: string): string => {
    const number = Number(value)
    if(!Number.isFinite(number)) return '0 ч'
    return `${number.toLocaleString('ru-RU', {maximumFractionDigits: 2})} ч`
}

const formatCount = (value: string): string => {
    const number = Number(value)
    if(!Number.isFinite(number)) return '0'
    return number.toLocaleString('ru-RU')
}

const formatPercent = (value: string, base?: string): string => {
    if(base !== undefined && Number(base) === 0) return '—'

    const number = Number(value)
    if(!Number.isFinite(number)) return '0%'
    return `${number.toLocaleString('ru-RU', {maximumFractionDigits: 2})}%`
}

const formatDateTime = (value: string): string => {
    const date = new Date(value)
    if(Number.isNaN(date.getTime())) return ''
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const isDebugEnabled = () => ['true', 'True', '1'].includes(process.env.REACT_APP_DEBUG ?? '')

const MetricCard = ({metric, tone, onClick, countLabel = 'занятий', tooltip}: {metric: IStatisticsMetric; tone?: 'good' | 'bad' | 'neutral'; onClick?: () => void; countLabel?: string; tooltip?: string}) => {
    const className = `${classes.metric} ${tone ? classes[tone] : ''} ${onClick ? classes.clickableMetric : ''}`
    const content = (
        <>
            <span className={classes.valueLabel}>
                {metric.label}
                {tooltip && (
                    <span className={classes.tooltip}>
                        <span tabIndex={0} className={classes.tooltipButton} aria-label={tooltip}>?</span>
                        <span className={classes.tooltipContent} role="tooltip">{tooltip}</span>
                    </span>
                )}
            </span>
            <strong>{formatHours(metric.hours)}</strong>
            <small>{Number(metric.count).toLocaleString('ru-RU')} {countLabel}</small>
            {onClick && <em>Подробнее</em>}
        </>
    )

    if(onClick){
        return (
            <button type="button" className={className} onClick={onClick}>
                {content}
            </button>
        )
    }

    return (
        <section className={className}>
            {content}
        </section>
    )
}

const RegularScheduleLossesModal = ({
    open,
    setOpen,
    items,
    isLoading,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    items: IRegularScheduleLossItem[];
    isLoading: boolean;
}) => (
    <Modal open={open} setOpen={setOpen}>
        <section className={classes.lossesModal}>
            <section className={classes.lossesModalHeader}>
                <section>
                    <h3>Сливы постоянного графика</h3>
                    <p>Студенты, у которых за выбранный период уменьшились часы постоянного графика.</p>
                </section>
                <button type="button" onClick={() => setOpen(false)} aria-label="Закрыть">×</button>
            </section>

            {isLoading ? (
                <section className={classes.lossesState}>Загружаем список...</section>
            ) : items.length === 0 ? (
                <section className={classes.lossesState}>Минусовых изменений за период нет.</section>
            ) : (
                <section className={classes.lossesList}>
                    {items.map(item => (
                        <section key={`${item.studentId}-${item.changedAt}-${item.lostHours}`} className={classes.lossItem}>
                            <section>
                                <strong>{item.studentName || `Студент #${item.studentId}`}</strong>
                                <small>{formatDateTime(item.changedAt)}</small>
                            </section>
                            <section className={classes.lossNumbers}>
                                <span>{formatHours(item.oldWeeklyHours)} → {formatHours(item.newWeeklyHours)}</span>
                                <b>-{formatHours(item.lostHours)}</b>
                            </section>
                        </section>
                    ))}
                </section>
            )}
        </section>
    </Modal>
)

const CancelledLessonsModal = ({
    open,
    setOpen,
    items,
    isLoading,
    faultParty,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    items: ICancelledLessonItem[];
    isLoading: boolean;
    faultParty: TStatisticsFaultParty;
}) => {
    const isStudentFault = faultParty === 'student'
    const title = isStudentFault ? 'Отмены по вине студентов' : 'Отмены по вине исполнителей'
    const emptyText = isStudentFault
        ? 'Отменённых уроков по вине студентов за период нет.'
        : 'Отменённых уроков по вине исполнителей за период нет.'

    return (
        <Modal open={open} setOpen={setOpen}>
            <section className={classes.lossesModal}>
                <section className={classes.lossesModalHeader}>
                    <section>
                        <h3>{title}</h3>
                        <p>Уроки, которые были отменены за выбранный период.</p>
                    </section>
                    <button type="button" onClick={() => setOpen(false)} aria-label="Закрыть">×</button>
                </section>

                {isLoading ? (
                    <section className={classes.lossesState}>Загружаем список...</section>
                ) : items.length === 0 ? (
                    <section className={classes.lossesState}>{emptyText}</section>
                ) : (
                    <section className={classes.lossesList}>
                        {items.map(item => (
                            <section key={`${item.eventId}-${item.startsAt}`} className={classes.lossItem}>
                                <section>
                                    <strong>{item.studentName || `Студент #${item.studentId}`}</strong>
                                    <small>{formatDateTime(item.startsAt)} · {formatHours(item.hours)}</small>
                                    <small>{item.tutorName || `Исполнитель #${item.tutorUserId}`}</small>
                                    {(item.tgAccountName || item.tagName) && (
                                        <small>{[item.tgAccountName, item.tagName].filter(Boolean).join(' · ')}</small>
                                    )}
                                    {item.comment && <small>{item.comment}</small>}
                                </section>
                                <section className={classes.lossNumbers}>
                                    <span>Отменено</span>
                                    <b>-{formatHours(item.hours)}</b>
                                </section>
                            </section>
                        ))}
                    </section>
                )}
            </section>
        </Modal>
    )
}

const ValueCard = ({label, value, caption, tone, tooltip}: {label: string; value: string; caption: string; tone?: 'good' | 'bad' | 'neutral'; tooltip?: string}) => (
    <section className={`${classes.metric} ${tone ? classes[tone] : ''}`}>
        <span className={classes.valueLabel}>
            {label}
            {tooltip && (
                <span className={classes.tooltip}>
                    <button type="button" className={classes.tooltipButton} aria-label={tooltip}>?</button>
                    <span className={classes.tooltipContent} role="tooltip">{tooltip}</span>
                </span>
            )}
        </span>
        <strong>{value}</strong>
        <small>{caption}</small>
    </section>
)

export default function StatisticsPage() {
    const {my} = useAppSelector(s => s.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [fromDate, setFromDate] = useState(getDefaultFrom)
    const [toDate, setToDate] = useState(getDefaultTo)
    const [tgAccountId, setTgAccountId] = useState('')
    const [filterOptions, setFilterOptions] = useState<IGetCalendarFilterOptionsResponse | null>(null)
    const [summary, setSummary] = useState<IStatisticsSummary>(EMPTY_STATISTICS_SUMMARY)
    const [regularScheduleLosses, setRegularScheduleLosses] = useState<IRegularScheduleLossItem[]>([])
    const [cancelledLessons, setCancelledLessons] = useState<ICancelledLessonItem[]>([])
    const [isLossesModalOpen, setIsLossesModalOpen] = useState(false)
    const [isLossesLoading, setIsLossesLoading] = useState(false)
    const [isCancelledLessonsModalOpen, setIsCancelledLessonsModalOpen] = useState(false)
    const [isCancelledLessonsLoading, setIsCancelledLessonsLoading] = useState(false)
    const [cancelledLessonsFaultParty, setCancelledLessonsFaultParty] = useState<TStatisticsFaultParty>('student')
    const [isLoading, setIsLoading] = useState(false)
    const [isRecalculating, setIsRecalculating] = useState(false)
    const [recalculateCooldownUntil, setRecalculateCooldownUntil] = useState(0)

    const isAccess = hasFeature(my, FEATURE_SECTIONS.STATISTICS) && hasFeature(my, FEATURES.STATISTICS.VIEW)
    const canDebugRecalculate = isDebugEnabled() && Date.now() >= recalculateCooldownUntil

    const handleError = useCallback((error: unknown, fallback: string) => {
        console.log(error)
        if(error instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: error.message, type: 'error'})
        }
        else if(error instanceof MyError){
            setGlobalMessage({message: error.message, type: 'error'})
        }
        else{
            setGlobalMessage({message: fallback, type: 'error'})
        }
    }, [setGlobalMessage, setIsAuth])

    const getStatisticsRequest = useCallback(() => {
        const range = getStatisticsRange(fromDate, toDate)
        if(!range) return null

        return {
            filter: {
                ...range,
                tgAccountIds: tgAccountId ? [tgAccountId] : [],
            },
        }
    }, [fromDate, tgAccountId, toDate])

    const loadFilterOptions = useCallback(async() => {
        try{
            const response = await calendarService.getCalendarFilterOptions({
                selected: {
                    tgAccountIds: [],
                    userIds: [],
                    studentIds: [],
                    tagIds: [],
                    includeCancelled: true,
                    includeRescheduled: true,
                },
            })
            setFilterOptions(response)
        }
        catch(error){
            handleError(error, 'Ошибка при загрузке фильтров статистики')
        }
    }, [handleError])

    const loadStatistics = useCallback(async() => {
        const request = getStatisticsRequest()
        if(!request) return

        try{
            setIsLoading(true)
            const response = await statisticsService.getStatistics(request)
            setSummary(response.summary)
        }
        catch(error){
            handleError(error, 'Ошибка при загрузке статистики')
        }
        finally{
            setIsLoading(false)
        }
    }, [getStatisticsRequest, handleError])

    const openRegularScheduleLosses = useCallback(async() => {
        const request = getStatisticsRequest()
        if(!request) return

        setIsLossesModalOpen(true)
        setIsLossesLoading(true)
        try{
            const response = await statisticsService.listRegularScheduleLosses(request)
            setRegularScheduleLosses(response.items)
        }
        catch(error){
            handleError(error, 'Ошибка при загрузке сливов постоянного графика')
            setRegularScheduleLosses([])
        }
        finally{
            setIsLossesLoading(false)
        }
    }, [getStatisticsRequest, handleError])

    const openCancelledLessons = useCallback(async(faultParty: TStatisticsFaultParty) => {
        const request = getStatisticsRequest()
        if(!request) return

        setCancelledLessonsFaultParty(faultParty)
        setIsCancelledLessonsModalOpen(true)
        setIsCancelledLessonsLoading(true)
        try{
            const response = await statisticsService.listCancelledLessons(request, faultParty)
            setCancelledLessons(response.items)
        }
        catch(error){
            handleError(error, 'Ошибка при загрузке отменённых уроков')
            setCancelledLessons([])
        }
        finally{
            setIsCancelledLessonsLoading(false)
        }
    }, [getStatisticsRequest, handleError])

    const debugRecalculateWeeklyPlanSnapshot = useCallback(async() => {
        if(!canDebugRecalculate) return

        try{
            setIsRecalculating(true)
            await statisticsService.debugRecalculateWeeklyPlanSnapshot()
            setRecalculateCooldownUntil(Date.now() + 60_000)
            setGlobalMessage({message: 'План недели пересчитан', type: 'ok'})
            await loadStatistics()
        }
        catch(error){
            handleError(error, 'Ошибка при пересчете плана недели')
        }
        finally{
            setIsRecalculating(false)
        }
    }, [canDebugRecalculate, handleError, loadStatistics, setGlobalMessage])

    useEffect(() => {
        if(isAccess) loadFilterOptions()
    }, [isAccess, loadFilterOptions])

    useEffect(() => {
        if(isAccess) loadStatistics()
    }, [isAccess, loadStatistics])

    if(!isAccess){
        return <AccessDeniedRedirect to={HOME_ROUTE.path} />
    }

    const tgAccounts = filterOptions?.tgAccounts ?? []
    const tgAccountItems = [
        {id: '', name: 'Все доступные'},
        ...tgAccounts.map(account => ({id: account.id, name: account.title || account.username})),
    ]

    return (
        <LayoutPages title={STATISTICS_ROUTE.name}>
            <section className={classes.container}>
                <section className={classes.headerCard}>
                    <section>
                        <h1>Статистика</h1>
                        <p>Обычные занятия за выбранный период. Пробные занятия не учитываются.</p>
                    </section>
                </section>

                <section className={classes.filters}>
                    <MyInput type="date" title="С даты" value={fromDate} setValue={setFromDate} />
                    <MyInput type="date" title="По дату" value={toDate} setValue={setToDate} />
                    <label className={classes.selectField}>
                        <span>Рабочий Telegram</span>
                        <DropDownListSelected
                            isLoading={false}
                            items={tgAccountItems}
                            selectedIdItems={[tgAccountId]}
                            onSelected={item => selected => {
                                if (selected) setTgAccountId(item.id)
                            }}
                            plainSelected
                        />
                    </label>
                    <section className={classes.actions}>
                        <MyButtonSecondary onClick={() => {
                            setFromDate(getDefaultFrom())
                            setToDate(getDefaultTo())
                            setTgAccountId('')
                        }}>
                            Сбросить
                        </MyButtonSecondary>
                        {isDebugEnabled() && (
                            <MyButtonSecondary
                                onClick={debugRecalculateWeeklyPlanSnapshot}
                                disabled={!canDebugRecalculate || isRecalculating}
                            >
                                {isRecalculating ? 'Пересчитываем...' : 'Пересчитать план недели'}
                            </MyButtonSecondary>
                        )}
                        <MyButton onClick={loadStatistics} isLoading={isLoading}>Показать</MyButton>
                    </section>
                </section>

                <section className={classes.group}>
                    <h2>План / факт</h2>
                    <section className={classes.grid}>
                        <MetricCard metric={summary.planned} tooltip="Все обычные и дополнительные занятия, запланированные в выбранном периоде." />
                        <ValueCard
                            label={summary.regularSchedulePlan.label}
                            value={isLoading ? '…' : formatHours(summary.regularSchedulePlan.hours)}
                            caption={isLoading ? 'Загружаем…' : `${formatCount(summary.regularSchedulePlan.count)} занятий за выбранный период`}
                            tooltip="Сумма часов всех учеников на выбранный диапазон которые назначены постоянным графиком"
                            tone="neutral"
                        />
                        <MetricCard metric={summary.completed} tone="good" tooltip="Обычные и дополнительные занятия, отмеченные проведёнными в выбранном периоде." />
                        <MetricCard metric={summary.completedRescheduled} tooltip="Перенесённые занятия, которые были фактически проведены в выбранном периоде." />
                        <MetricCard metric={summary.extraLessons} tooltip="Разовые дополнительные занятия в выбранном периоде." />
                    </section>
                </section>

                <section className={classes.group}>
                    <h2>Отмены и переносы</h2>
                    <section className={classes.grid}>
                        <MetricCard metric={summary.studentCancelled} tone="bad" onClick={() => openCancelledLessons('student')} tooltip="Отмены, где причиной указан студент." />
                        <MetricCard metric={summary.tutorCancelled} tone="bad" onClick={() => openCancelledLessons('tutor')} tooltip="Отмены, где причиной указан исполнитель." />
                        <MetricCard metric={summary.studentRescheduled} tooltip="Переносы занятий по причине со стороны студента." />
                        <MetricCard metric={summary.tutorRescheduled} tooltip="Переносы занятий по причине со стороны исполнителя." />
                        <MetricCard metric={summary.unknownEvents} tone="neutral" tooltip="События без однозначно указанной причины." />
                    </section>
                </section>

                <section className={classes.group}>
                    <h2>Движение часов</h2>
                    <section className={classes.grid}>
                        <MetricCard metric={summary.makeups} tooltip="Отработки за выбранный период." />
                        <MetricCard metric={summary.lostHours} tone="bad" tooltip="Потерянные часы из отмен и переносов." />
                        <MetricCard metric={summary.regularScheduleLostHours} tone="bad" onClick={openRegularScheduleLosses} countLabel="изменений" tooltip="Снижение часов постоянного графика из-за изменения или удаления расписания." />
                        <MetricCard metric={summary.gainedHours} tone="good" tooltip="Добавленные часы из изменений графика." />
                        <section className={classes.net}>
                            <span>Разница <span className={classes.tooltip}><button type="button" className={classes.tooltipButton} aria-label="Добавленные часы минус потерянные часы">?</button><span className={classes.tooltipContent} role="tooltip">Добавленные часы минус потерянные часы</span></span></span>
                            <strong>{formatHours(summary.netHours)}</strong>
                        </section>
                    </section>
                </section>

                <section className={classes.group}>
                    <h2>Конверсия</h2>
                    <section className={classes.grid}>
                        <ValueCard
                            label="Запланировано пробных"
                            value={formatCount(summary.conversion.plannedTrials)}
                            tooltip="Все назначенные пробные занятия в выбранном периоде."
                            caption="пробных занятий"
                        />
                        <ValueCard
                            label="Создано студентов"
                            value={formatCount(summary.conversion.createdStudents)}
                            tooltip="Ученики, созданные из пробных занятий выбранного периода."
                            caption="из этих пробных"
                            tone="good"
                        />
                        <ValueCard
                            label="Конверсия по назначенным"
                            value={formatPercent(summary.conversion.rate, summary.conversion.plannedTrials)}
                            caption="созданные студенты / назначенные пробные"
                            tone="neutral"
                            tooltip="Созданные ученики ÷ все назначенные пробные занятия"
                        />
                        <ValueCard
                            label="Проведено пробных"
                            value={formatCount(summary.conversion.completedTrials)}
                            tooltip="Пробные с сохранённой отметкой проведения, включая слив после проведения."
                            caption="пробных занятий"
                        />
                        <ValueCard
                            label="Конверсия по проведённым"
                            value={formatPercent(summary.conversion.completedRate, summary.conversion.completedConversionBase)}
                            caption="созданные студенты / проведённые пробные"
                            tone="neutral"
                            tooltip="Созданные ученики ÷ (проведённые пробные − сливы до пробного)"
                        />
                    </section>
                </section>
            </section>

            <RegularScheduleLossesModal
                open={isLossesModalOpen}
                setOpen={setIsLossesModalOpen}
                items={regularScheduleLosses}
                isLoading={isLossesLoading}
            />
            <CancelledLessonsModal
                open={isCancelledLessonsModalOpen}
                setOpen={setIsCancelledLessonsModalOpen}
                items={cancelledLessons}
                isLoading={isCancelledLessonsLoading}
                faultParty={cancelledLessonsFaultParty}
            />
        </LayoutPages>
    )
}
